import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormlyErrorComponent } from '../formly-error.component';
import { FieldLabelRowComponent } from './field-label-row.component';
import { distinctUntilChanged, takeUntil } from 'rxjs';
import { DateHelperService } from '../../services/date-helper.service';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { ClearableFieldType } from './clearable-field.type';

interface OpenableDatepicker {
  open(): void;
}

@Component({
  selector: 'formly-field-date',
  template: `
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="formControl.invalid && formControl.touched">
      <mat-label>
        <app-field-label-row
          [label]="field.props.label"
          [requiredStar]="!!field.props.required"
          [required]="field.props.required"
          [info]="props.info"></app-field-label-row>
      </mat-label>
      <input
        #dateInput
        type="text"
        [formlyDataCy]="field"
        matInput
        [readonly]="isEmbedded"
        [formControl]="formControl"
        [formlyAttributes]="field"
        [matDatepicker]="picker"
        (click)="openPicker(dateInput, picker)" />
      <!-- Toggle and clear share one trailing cell. Filled + blurred shows clear; focusing the
           field restores the calendar toggle. Clearing opens the picker as prescribed. -->
      <span class="dd-clear-suffix dd-addon-suffix" matSuffix>
        <mat-datepicker-toggle class="dd-addon-suffix__passive" [for]="picker">
          <!-- Outline calendar (Figma lucide, 20px #545559) instead of the filled Material glyph. -->
          <mat-icon matDatepickerToggleIcon class="dd-date-toggle-icon" svgIcon="heroicons_outline:calendar"></mat-icon>
        </mat-datepicker-toggle>
        <app-clear-field-button
          [field]="field"
          [hasValue]="hasClearableValue()"
          [disabled]="isDisabled()"
          [ariaLabel]="clearAriaLabel()"
          [focusTarget]="dateInput"
          (clearSelection)="clearAndOpenPicker(dateInput, picker)" />
      </span>
      <mat-datepicker #picker [startAt]="formControl.value"></mat-datepicker>
      <!-- Material allows one hint per align slot. With a description, that text owns the start slot
           and the input-format hint yields to the end slot (the same pattern as the counter in
           max-length-counter.wrapper.ts); without one the format hint stays in the start slot, as in
           datetime-input.component.ts. Two start hints made Material throw
           getMatFormFieldDuplicatedHintError in dev mode and jammed both texts together in optimised
           builds (#22600).
           A control-flow block whose root must project into a selector slot may hold exactly ONE
           root element. mat-form-field projects hints with
           <ng-content select="mat-hint:not([align='end'])"> and select="mat-hint[align='end']";
           for an @if/@for/@switch block the compiler (ingestControlFlowInsertionPoint) copies the
           tag name and attributes of the block's root into the projection match only when there is
           a single root. With two, neither hint matches and both fall into the wildcard slot of the
           infix, inside the input area instead of under the field. So the start hint is an
           unconditional element and only the end hint sits in a block. The align must stay a static
           attribute: the selector matches the attribute, a binding would not match.
           A placeholder that is the format itself already shows it inside the field (Figma), so the hint line yields to it. -->
      <mat-hint>{{ field.props.description || startHint }}</mat-hint>
      @if (field.props.description) {
        <mat-hint align="end">{{ formatHint }}</mat-hint>
      }
    </mat-form-field>
    <app-formly-error [field]="field"></app-formly-error>
  `,
  styles: `
    .dd-date-toggle-icon {
      width: 20px;
      height: 20px;
      color: var(--dd-accent);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    FormsModule,
    FormlyErrorComponent,
    FormlyDataCyDirective,
    ClearFieldButtonComponent,
    FieldLabelRowComponent,
  ],
})
export class FormlyFieldDateComponent extends ClearableFieldType<FieldTypeConfig> implements OnInit {
  // Single source for the input-format hint, which renders in either the start or the end slot.
  protected readonly formatHint = 'DD-MM-YYYY';
  /** The start hint: the format, unless the placeholder already shows the format (Dutch or English).
   *  Resolved once at init: the placeholder is static field config; a builder preview re-creates the field. */
  protected startHint = this.formatHint;
  isEmbedded = !!window.flutter_inappwebview;
  destroy$ = injectDestroy();
  constructor() {
    super();
  }

  ngOnInit() {
    if (/^DD-MM-(YYYY|JJJJ)$/i.test(String(this.props.placeholder ?? ''))) this.startHint = '';
    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe(value => {
      if (value && value?.ts != undefined) {
        const date = new Date(value?.ts);
        // Model-only rewrite: writing back to the input would reformat it mid-typing (#22991)
        this.formControl.setValue(DateHelperService.toLocalISOString(date), {
          emitEvent: false,
          emitModelToViewChange: false,
        });
      }
    });
  }

  // Open picker while maintaining input focus
  openPicker(input: HTMLInputElement, picker: OpenableDatepicker): void {
    picker.open();
    // Maintain focus on input after picker opens
    setTimeout(() => input.focus(), 32);
  }

  clearAndOpenPicker(input: HTMLInputElement, picker: OpenableDatepicker): void {
    super.clear();
    this.openPicker(input, picker);
  }
}
